// Survivor Rank (krug 5, F12): progress after level 20. The level cap stays 20; from there
// XP fills the rank, 9,000 XP a rank (K5.2; 3,000 in F12), without end. The rewards never change combat strength:
// a box with an item every rank, a cosmetic every 5 ranks (a title or a share card frame from
// a fixed list), and +1% loot every 10 ranks, at most +10% (rank 100). Rank is not part of
// Power or of matchmaking. On level 20 the survivor's `xp` holds all the XP earned since
// (before krug 5 it was reset to 0 there), so the rank needs no column of its own.

import { GAME } from './constants';
import type { AchievementDef } from './content/achievements';
import type { Rarity } from './types';

export const RANK_RULES = {
  /**
   * K5.2: 3,000 → 9,000 (runs play out since K4.4, so a rank came almost every day). The rank is
   * read from the stored XP, so a survivor already on level 20 gets it recounted; rewards already
   * paid (`record.ranksPaid`) stay, and new ones start above that rank.
   */
  xpPerRank: 9000,
  /** +1% loot every 10 ranks, at most +10%. */
  lootPerStep: 0.01,
  lootEvery: 10,
  lootMax: 0.1,
  cosmeticEvery: 5,
  /** Item level of the box item: the level cap. */
  boxLevel: 20,
} as const;

/**
 * What a rank box holds (weights). 4. 10. 2026 (package 3, 7.g, owner decision): no Uncommon: a Rare item 70, an Epic
 * 25, or a pack of 10 Relic Shards 5; never Legendary. Before (F12): Uncommon 65 · Rare 30 · Epic 5 (`useRulesBefore4Oct`).
 */
export const RANK_BOX = {
  table: [
    ['rare', 70],
    ['epic', 25],
    ['shards', 5],
  ] as [Rarity | 'shards', number][],
  shards: 10,
};

/**
 * 4. 10. 2026 (package 3, G): the first ranks of every season take less XP (6,000 instead of 9,000), so the first
 * Marks of the Veteran Ledger come soon after level 20. `on: false` (simulations, `useRulesBefore4Oct`): 9,000 each.
 */
export const RANK_FAST = { on: true, ranks: 5, xp: 6000 };

/**
 * Ranks from this season's XP (0 below level 20). The level and XP go back to the start with every season's wipe,
 * so this counts the season; `base` is the ranks of the seasons before (the rank stays with the account).
 */
export function rankOf(level: number, xp: number, base = 0): number {
  return Math.max(0, base) + rankProgress(level, xp).rank;
}

/** This season's rank, the XP inside it and the XP it takes (all 0 below level 20). `base` as in `rankOf`. */
export function rankProgress(level: number, xp: number, base = 0): { rank: number; xp: number; next: number } {
  if (level < GAME.LEVEL_CAP) return { rank: Math.max(0, base), xp: 0, next: 0 };
  const x = Math.max(0, xp);
  const fast = RANK_FAST.on ? RANK_FAST.ranks * RANK_FAST.xp : 0;
  if (x < fast) return { rank: Math.max(0, base) + Math.floor(x / RANK_FAST.xp), xp: x % RANK_FAST.xp, next: RANK_FAST.xp };
  const rest = x - fast;
  return { rank: Math.max(0, base) + (RANK_FAST.on ? RANK_FAST.ranks : 0) + Math.floor(rest / RANK_RULES.xpPerRank), xp: rest % RANK_RULES.xpPerRank, next: RANK_RULES.xpPerRank };
}

/** Extra loot from the rank (0.03 = +3%): +1% every 10 ranks, at most +10%. */
export function rankLoot(rank: number): number {
  return Math.min(RANK_RULES.lootMax, Math.floor(Math.max(0, rank) / RANK_RULES.lootEvery) * RANK_RULES.lootPerStep);
}

export interface RankCosmetic {
  rank: number;
  kind: 'title' | 'frame';
  /** The title's text, or the frame id (`rank_10`…). */
  value: string;
}

/** The fixed list: a title and a share card frame in turn, up to rank 100; ranks go on without new ones. */
export const RANK_COSMETICS: RankCosmetic[] = [
  { rank: 5, kind: 'title', value: 'Seasoned' },
  { rank: 10, kind: 'frame', value: 'rank_rust' },
  { rank: 15, kind: 'title', value: 'Road-Worn' },
  { rank: 20, kind: 'frame', value: 'rank_bronze' },
  { rank: 25, kind: 'title', value: 'Old Hand' },
  { rank: 30, kind: 'frame', value: 'rank_iron' },
  { rank: 35, kind: 'title', value: 'Last Light' },
  { rank: 40, kind: 'frame', value: 'rank_silver' },
  { rank: 45, kind: 'title', value: 'Grizzled' },
  { rank: 50, kind: 'frame', value: 'rank_gold' },
  { rank: 55, kind: 'title', value: 'Unkillable' },
  { rank: 60, kind: 'frame', value: 'rank_ember' },
  { rank: 65, kind: 'title', value: 'Wasteland Saint' },
  { rank: 70, kind: 'frame', value: 'rank_jade' },
  { rank: 75, kind: 'title', value: 'Myth' },
  { rank: 80, kind: 'frame', value: 'rank_obsidian' },
  { rank: 85, kind: 'title', value: 'Undying' },
  { rank: 90, kind: 'frame', value: 'rank_aurora' },
  { rank: 95, kind: 'title', value: 'The Last One' },
  { rank: 100, kind: 'frame', value: 'rank_crown' },
];

export const rankAchievementId = (rank: number) => `rank_${rank}`;

/** Rank cosmetics as achievements (merged into ACHIEVEMENTS): titles through `title`, frames by the server. */
export const RANK_ACHIEVEMENTS: Record<string, AchievementDef> = Object.fromEntries(
  RANK_COSMETICS.map((c): [string, AchievementDef] => {
    const id = rankAchievementId(c.rank);
    return c.kind === 'title'
      ? [id, { id, name: `Survivor Rank ${c.rank}`, description: `Reach Survivor Rank ${c.rank}.`, title: c.value }]
      : [id, { id, name: `Survivor Rank ${c.rank}`, description: `Reach Survivor Rank ${c.rank}. Unlocks a share card frame.` }];
  }),
);
